'use client';

import {GUIDE_ANON_EXPERIENCE_GATE_EXPERIMENT} from 'common/experiments';
import {recordExperimentExposure} from 'iq-ab_testing/api';
import {useAuth} from 'iq-auth';
import {CoderUpsellBanner} from 'iq-interview_guides/components/coder_upsell_banner';
import {PremiumReminderBanner} from 'iq-interview_guides/components/premium_reminder_modal';
import {trackGuideExperiencePaywallViewed} from 'iq-interview_guides/tracking';
import type {InterviewGuideTabProps} from 'iq-interview_guides/types';
import {useEffect, useState} from 'react';
import {usePathname, useSearchParams} from 'next/navigation';
import {Spacer} from 'node_modules/@repo/ui/src/spacer';
import {Paginator} from 'common/components/paginator';
import UnlockInterviewGuide from 'iq-upselling/components/unlock_interview_guide';
import ExperienceGateCard from './experience_gate_card';
import ExperiencesPaywall from './experiences_paywall';
import InterviewExperienceCard from './interview_experience_card';
import InterviewExperiencesSummary from './interview_experiences_summary';
import {SubmitExperienceModal} from './submit_experience_modal';
import SubmitExperienceBanner from './submit_experience_banner';
import {usePremiumReminderDismissed} from 'iq-interview_guides/hooks';

const PAGE_SIZE = 8;

export const InterviewExperiencesTab = ({interviewGuide, experienceGate}: InterviewGuideTabProps) => {
  const visibleExperiencesCount = interviewGuide.experiences.length;
  const {isCoder, isPremium, user} = useAuth();
  const lockedExperiences = interviewGuide.experiences.filter((e) => e.body === null);
  // guide_anon_experience_gate_v1, experiment arm, anonymous visitor: the report list is
  // replaced by a signup card (the page already stripped every report's text).
  const showExperienceGateCard =
    experienceGate?.variant === 'experiment' && experienceGate.gateMode === 'public';

  // Exposure for guide_anon_experience_gate_v1, once per page view, in both arms. Every
  // tab is mounted on load (inactive ones are only hidden), so this fires whichever tab
  // the visitor lands on.
  const gateVariant = experienceGate?.variant;
  const gateMode = experienceGate?.gateMode;
  const lockedCount = lockedExperiences.length;
  useEffect(() => {
    if (!gateVariant || !gateMode) return;
    void trackGuideExperiencePaywallViewed({
      slug: interviewGuide.slug,
      variant: gateVariant,
      gateMode,
      lockedCount,
    });
    void recordExperimentExposure(GUIDE_ANON_EXPERIENCE_GATE_EXPERIMENT, gateVariant);
  }, [gateVariant, gateMode, lockedCount, interviewGuide.slug]);
  const coderBeyondLimit = isCoder && interviewGuide.lock_status !== 'fully-unlocked';

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Arriving with ?unlock=1 (e.g. from a locked report on the company guide) auto-opens the upsell.
  const [unlockOpen, setUnlockOpen] = useState(
    () =>
      searchParams.get('unlock') === '1' &&
      interviewGuide.lock_status === 'partially-unlocked' &&
      !isPremium,
  );
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('exp_page') ?? '0', 10);
    return isNaN(p) || p < 0 ? 0 : p;
  });
  const onShare = () => setShareModalOpen(true);

  const pagedExperiences = interviewGuide.experiences.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE,
  );

  function handlePageChange(newPage: number) {
    setPage(newPage);
    const params = new URLSearchParams(window.location.search);
    if (newPage === 0) {
      params.delete('exp_page');
    } else {
      params.set('exp_page', String(newPage));
    }
    const search = params.toString();
    window.history.replaceState(null, '', `${pathname}${search ? `?${search}` : ''}`);
  }

  function experiencePageHref(targetPage: number) {
    const params = new URLSearchParams(searchParams.toString());
    if (targetPage === 0) {
      params.delete('exp_page');
    } else {
      params.set('exp_page', String(targetPage));
    }
    const search = params.toString();
    return `${pathname}${search ? `?${search}` : ''}`;
  }
  const {premiumReminderIsDismissed, dismissPremiumReminder} = usePremiumReminderDismissed(
    interviewGuide.slug,
  );

  return (
    <div className='w-full'>
      {coderBeyondLimit && <CoderUpsellBanner onShare={onShare} />}

      <InterviewExperiencesSummary
        editorialTitle={interviewGuide.data.editorial_title}
        editorial={interviewGuide.data.editorial}
        totalCount={interviewGuide.data.interview_experiences_count}
      />

      <div className='min-[960px]:hidden '>
        <SubmitExperienceBanner data={interviewGuide} variant='inline' />
        <Spacer size='xl' />
      </div>

      {!premiumReminderIsDismissed && (isPremium || user.isAdmin) && (
        <PremiumReminderBanner
          companyName={interviewGuide.company_name}
          onClose={dismissPremiumReminder}
          onShare={() => {
            dismissPremiumReminder();
            onShare();
          }}
        />
      )}

      <h2 className='text-xl font-bold text-iq-black mb-1'>What candidates actually experienced</h2>
      <p className='text-sm text-iq-grey-medium mb-6'>
        Real interview reports from people who went through the {interviewGuide.company_name}{' '}
        process.
      </p>

      <div className='flex flex-col gap-3'>
        {showExperienceGateCard ? (
          <ExperienceGateCard
            companyName={interviewGuide.company_name}
            positionName={interviewGuide.position_name}
            experiences={interviewGuide.experiences}
            guideSlug={interviewGuide.slug}
            variant={experienceGate.variant}
          />
        ) : interviewGuide.experiences.length === 0 ? (
          <div className='bg-gray-50 border border-iq-grey-light rounded-lg p-8 text-center'>
            <h3 className='text-lg font-bold text-iq-black mb-2'>No experiences yet</h3>
            <p className='text-sm text-iq-grey-medium mb-6'>
              Be the first to share your {interviewGuide.company_name} interview experience and help
              others prepare.
            </p>
            <button
              onClick={onShare}
              className='px-6 py-2.5 bg-iq-orange text-white text-sm font-semibold rounded-md hover:opacity-90 transition-opacity'>
              Share my experience
            </button>
          </div>
        ) : (
          pagedExperiences.map((interviewExperience) => (
            <InterviewExperienceCard
              key={interviewExperience.id}
              guideSlug={interviewGuide.slug}
              positionName={interviewGuide.position_name}
              positionDisplayName={interviewExperience.position_display_name}
              positionSlug={interviewGuide.position_slug}
              interviewExperience={interviewExperience}
              companySlug={interviewGuide.company_slug}
              companyName={interviewGuide.company_name}
              visibleExperiencesCount={visibleExperiencesCount}
              companyId={interviewGuide.company_id}
              positionId={interviewGuide.position_id}
            />
          ))
        )}
      </div>

      {!showExperienceGateCard && (
        <>
          <div className='mt-4'>
            <Paginator
              page={page}
              pageSize={PAGE_SIZE}
              total={visibleExperiencesCount}
              onPageChange={handlePageChange}
              hrefBuilder={experiencePageHref}
            />
          </div>

          <ExperiencesPaywall
            companyName={interviewGuide.company_name}
            companySlug={interviewGuide.company_slug}
            positionSlug={interviewGuide.position_slug}
            positionName={interviewGuide.position_name}
            totalCount={visibleExperiencesCount}
            companyId={interviewGuide.company_id}
            positionId={interviewGuide.position_id}
            experienceShared={interviewGuide.experience_shared}
            lockStatus={interviewGuide.lock_status}
            lockedExperiences={lockedExperiences}
            experienceGate={experienceGate ?? null}
          />
        </>
      )}

      {unlockOpen && (
        <UnlockInterviewGuide
          company={interviewGuide.company_name}
          totalCount={visibleExperiencesCount}
          onClose={() => setUnlockOpen(false)}
          onShare={() => {
            setUnlockOpen(false);
            setShareModalOpen(true);
          }}
        />
      )}

      {shareModalOpen && (
        <SubmitExperienceModal
          guideSlug={interviewGuide.slug}
          onClose={() => setShareModalOpen(false)}
          defaultCompanySlug={interviewGuide.company_slug}
          defaultPositionSlug={interviewGuide.position_slug}
          companyId={interviewGuide.company_id}
          positionId={interviewGuide.position_id}
        />
      )}
    </div>
  );
};
